import React from "react";
import {
  ResponsiveContainer,
  AreaChart as ReAreaChart,
  Area,
  Tooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
} from "recharts";
import { Inbox } from "lucide-react";
import { useTheme } from "src/hooks/useTheme";

type AreaSpec = {
  dataKey: string;
  stroke: string;
  fill?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  dot?: boolean;
  type?: "monotone" | "linear" | "step";
};

interface AreaChartWrapperProps {
  data: Array<Record<string, any>>;
  xKey?: string;
  areas?: AreaSpec[];
  containerClassName?: string;
  hideXAxis?: boolean;
  hideYAxis?: boolean;
  xAxisProps?: Record<string, any>;
  yAxisProps?: Record<string, any>;
  tooltipFormatter?: (value?: number) => string | [string, string];
  tooltipLabelFormatter?: (label?: string) => string;
  showLegend?: boolean;
  legendProps?: Record<string, any>;
  xTickFormatter?: (value: string) => string;
  rotateXAxisLabels?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
}

const DEFAULT_AREAS: AreaSpec[] = [
  {
    dataKey: "stockIn",
    stroke: "#16A34A",
    strokeWidth: 2,
    fill: "url(#stockInFill)",
    dot: false,
    type: "monotone",
  },
  {
    dataKey: "stockOut",
    stroke: "#DC2626",
    strokeWidth: 2,
    fill: "url(#stockOutFill)",
    dot: false,
    type: "monotone",
  },
];

const AreaChartWrapper: React.FC<AreaChartWrapperProps> = ({
  data,
  xKey = "date",
  areas,
  containerClassName = "mt-4 h-44 w-full",
  hideXAxis = false,
  hideYAxis = false,
  xAxisProps,
  yAxisProps,
  tooltipFormatter,
  tooltipLabelFormatter,
  showLegend = false,
  legendProps,
  xTickFormatter,
  rotateXAxisLabels = false,
  isEmpty = false,
  emptyMessage,
}) => {
  const resolvedAreas = areas && areas.length ? areas : DEFAULT_AREAS;
  const usesDefaultAreas = resolvedAreas === DEFAULT_AREAS;
  const xAxisReservedHeight = rotateXAxisLabels ? 36 : 0;
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const gridStroke = isDark ? "#374151" : "#E5E7EB";

  return (
    <div className={`relative ${containerClassName}`}>
      <ResponsiveContainer width="100%" height="100%">
        <ReAreaChart
          data={data}
          margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0,
          }}
        >
          {usesDefaultAreas && (
            <defs>
              <linearGradient id="stockInFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#4ADE80" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#4ADE80" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="stockOutFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F87171" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#F87171" stopOpacity={0.05} />
              </linearGradient>
            </defs>
          )}
          <CartesianGrid stroke={gridStroke} vertical={false} />
          <XAxis
            dataKey={xKey}
            hide={hideXAxis}
            tickFormatter={xTickFormatter}
            tick={{ fontSize: 11, fill: "#9CA3AF" }}
            tickLine={false}
            axisLine={{ stroke: gridStroke }}
            {...(rotateXAxisLabels
              ? { angle: -45, textAnchor: "end", height: xAxisReservedHeight }
              : {})}
            {...xAxisProps}
          />
          <YAxis hide={hideYAxis} {...yAxisProps} />
          <Tooltip
            contentStyle={{
              borderRadius: "8px",
              borderColor: isDark ? "#374151" : "#E5E7EB",
              backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
              color: isDark ? "#E5E7EB" : "#111827",
              fontSize: "12px",
            }}
            labelStyle={{ fontWeight: 600, color: isDark ? "#F3F4F6" : "#111827" }}
            itemStyle={{ color: isDark ? "#E5E7EB" : "#111827" }}
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
          />
          {showLegend && <Legend {...legendProps} />}
          {!isEmpty &&
            resolvedAreas.map((area) => (
              <Area
                key={area.dataKey}
                type={area.type || "monotone"}
                dataKey={area.dataKey}
                stroke={area.stroke}
                strokeWidth={area.strokeWidth ?? 2}
                fill={area.fill || area.stroke}
                fillOpacity={area.fillOpacity}
                dot={area.dot ?? false}
              />
            ))}
        </ReAreaChart>
      </ResponsiveContainer>
      {isEmpty && (
        <div
          className="pointer-events-none absolute inset-x-0 top-0 flex flex-col items-center justify-center gap-2"
          style={{ bottom: xAxisReservedHeight }}
        >
          <div className="rounded-full bg-gray-50 p-2.5 dark:bg-gray-700">
            <Inbox size={18} className="text-gray-300 dark:text-gray-400" />
          </div>
          {emptyMessage && (
            <span className="text-xs font-medium text-gray-400 dark:text-gray-400">
              {emptyMessage}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default AreaChartWrapper;
